import React, { useEffect, useMemo, useState } from 'react';

import {
  arrayToSimpleObj,
  echartsContent,
  getPercentageInfo,
  handleMentionData,
} from '@/pages/opinion/utils';

import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { chartAllColors } from '@/pages/opinion/constants';
import { getLocale } from 'umi';
import {
  formatTextInfoSimpleFunc,
  formatTimeXAxisText,
} from '@/pages/opinion/utils/chart';
import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import { formatDateFunc } from '@op/utils/time';
import { TRequestBackType } from '@op/utils/requestUtil';

interface ITopicChartProps {
  data: any[] | TRequestBackType;
  filter: any;
  formatMessage: any;
  sumData: any;
  translationData: any;
  type: 'bar' | 'line';
  handleClickInfo: any;
}

export default function Topic(props: ITopicChartProps) {
  // 元数据处理，需要获得 chartData, legendData, xAxisData
  const {
    data,
    filter,
    formatMessage,
    sumData,
    translationData,
    type,
    handleClickInfo,
  } = props;

  const [clickInfo, setClickInfo] = useState({
    dataIndex: null,
    seriesIndex: null,
    seriesName: null,
    name: null,
  });

  const barSeriesObj = useMemo(() => {
    if (!data)
      return {
        newArr: [],
        timeArr: [],
        totalArr: [],
      };

    // 数组类型数据，兼容老版本数据
    if (Array.isArray(data)) {
      const axisTimeTypeMap: {
        [key: string]: 'day' | 'year' | 'days' | 'hourly';
      } = {
        daily: 'days',
        weekly: 'days',
        hourly: 'hourly',
        year: 'year',
      };
      const axisTimeType = axisTimeTypeMap[filter.date_type] || 'year';
      return handleMentionData(data, axisTimeType);
    }

    // 新版本数据用这个处理
    const { series, totalSeries, xAxis, legend } = data || {};
    return {
      newArr: series || [],
      timeArr: xAxis || [],
      totalArr: totalSeries || [],
      legend,
    };
  }, [data, filter]);

  const dataFunc = formatDateFunc(filter.date_type);
  const timeTotalMap = arrayToSimpleObj(sumData, 'value', (item: any) =>
    dataFunc(item.time),
  );

  const locale = getLocale();
  const translationFunc = useMemo(() => {
    const func = formatTextInfoSimpleFunc(translationData, locale);

    return (value: string): string => {
      const key = value?.toLocaleLowerCase();
      return func(key);
    };
  }, [translationData, locale]);

  function onClick(param: any) {
    const { seriesIndex, dataIndex, value, seriesName, name } = param;
    if (
      clickInfo.dataIndex === dataIndex &&
      clickInfo.seriesIndex === seriesIndex
    ) {
      const newClickInfo = {
        seriesIndex: null,
        dataIndex: null,
        seriesName: null,
        name: null,
        except_others: null,
        except_topic: null,
      };
      console.log('same and skip');
      setClickInfo(newClickInfo);
      handleClickInfo?.(newClickInfo);
    } else {
      const newClickInfo = {
        seriesIndex,
        dataIndex,
        seriesName,
        name,
      };
      console.log('not same and set');
      setClickInfo(newClickInfo);

      let exceptList = null;

      if (seriesName === 'Others') {
        exceptList = barSeriesObj.newArr
          ?.map((v) => v.name)
          ?.filter((v) => v !== 'Others');
      }

      handleClickInfo?.({
        ...newClickInfo,
        except_others: exceptList,
        except_topic: exceptList,
        translationData: {
          ...translationData,
          others: {
            'zh-CN': '其他',
            'en-US': 'Others',
          },
        },
      });
    }
  }

  const [options, setOptions] = useState({});

  useEffect(() => {
    const hasClickInfo =
      clickInfo !== null &&
      clickInfo.seriesIndex !== null &&
      clickInfo.dataIndex !== null;

    // 堆叠图的附加配置
    const barOptionOtherInfo: any = {
      type,
      barMaxWidth: '70px',
    };

    if (type === 'bar') {
      barOptionOtherInfo.stack = 'overviewBar';
    }

    const barSeriesArr = barSeriesObj?.newArr
      ?.filter((item) => item.name !== 'Others')
      ?.map((v) => ({
        ...v,
        ...barOptionOtherInfo,
      }));
    const xAxisData = barSeriesObj.timeArr;

    const localLegendData: {
      name: any;
      icon: string;
      itemStyle: { color: string };
    }[] = [];
    const newChartData = barSeriesArr.map((item, index) => {
      const color = chartAllColors[index];
      localLegendData.push({
        name: item.name,
        icon: 'circle',
        itemStyle: { color },
      });
      return {
        ...item,
        data: item.data.map(
          (itemValue: { value: number } | number, k: number) => {
            const value =
              typeof itemValue === 'number' ? itemValue : itemValue.value;
            if (!hasClickInfo) {
              return { value, itemStyle: { color } };
            }
            const { seriesIndex, dataIndex } = clickInfo;
            if (seriesIndex === index && dataIndex === k) {
              return {
                value,
                itemStyle: {
                  borderColor: '#000',
                  borderWidth: 2,
                  color,
                  opacity: 1,
                },
                emphasis: {
                  itemStyle: {
                    borderColor: '#000',
                    borderWidth: 2,
                    opacity: 1,
                  },
                },
              };
            }

            return {
              value,
              itemStyle: { opacity: 0.1, color },
            };
          },
        ),
      };
    });

    const newLegendData = barSeriesObj?.legend || localLegendData;

    const opts = {
      title: {
        text: formatMessage({ id: 'Mentions' }),
        textStyle: {
          fontWeight: '400',
          fontSize: 12,
          color: '#838697',
        },
        padding: 0,
        top: '2%',
        left: '0%',
      },
      tooltip: {
        formatter: (params: any) => {
          let newParams = Array.isArray(params) ? [...params] : [params];
          const { name: time } = params?.[0] || {};
          // let forData = barSeriesArr.filter((v) => v.name !== 'total总数'); // 过滤掉 'total总数'的柱子
          const total = timeTotalMap[time] || 0;
          // 如果有选中信息，并且hover的时间和点击的时间不同，那么就不展示弹窗
          if (hasClickInfo && time !== clickInfo.name) return null;
          // 如果点击了，并且还在当前时间轴内，就直接展示点击数据的弹窗
          if (hasClickInfo) {
            newParams = params.filter(
              (v: { seriesName: string }) =>
                v.seriesName === clickInfo.seriesName,
            );
          }
          return formatChartParamsDate({
            params: newParams,
            total,
            listTitleFunc: (item: any) => {
              return translationFunc(item.seriesName);
            },
            isSort: true,
            dealwithRecords(records, total) {
              return records.map((item) => {
                return {
                  ...item,
                  share: getPercentageInfo(item.originValue, total, 2, '-'),
                };
              });
            },
            tablesConfigFunc: (records) => {
              const columns = [
                {
                  key: 'text',
                  header: listTimeTitleFunc(
                    time,
                    filter?.date_type || 'daily',
                    true,
                  ),
                },
                {
                  key: 'value',
                  header: formatMessage({ id: 'Mentions' }),
                  weight: 'bold',
                  align: 'right',
                },
              ];

              if (!hasClickInfo) {
                columns.push({
                  key: 'share',
                  header: formatMessage({ id: 'Share-1' }),
                  weight: 'bold',
                  align: 'right',
                });
              }
              return [{ records, columns }];
            },
          });
        },
      },
      grid: {
        right: '1%',
        bottom: '10%',
        top: '15%',
        containLabel: true,
      },
      legend: {
        data: newLegendData,
        formatter: (value: string) => {
          return translationFunc(value);
        },
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, filter?.date_type || 'daily');
      }),
      xAxis: {
        data: xAxisData,
        axisLabel: {
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter.date_type),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => {
            return echartsContent(value);
          },
        },
      },
      series: newChartData,
    };

    setOptions(opts);
  }, [barSeriesObj, locale, sumData, clickInfo, type]);

  return (
    <EChartCommon
      options={options}
      height={327}
      optionsProps={{ type }}
      eventsDict={{
        click: onClick,
      }}
    ></EChartCommon>
  );
}
